// frontend/src/pages/ProductsPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react'; // Importa todos os hooks necessários
import api from '../services/api';
import { PhotoIcon, PencilSquareIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

// Definir a URL base do backend para carregar imagens
const BACKEND_URL = api.defaults.baseURL.replace('/api', ''); // Ajuste se a porta/URL do seu backend for diferente

// ==================================================
// === COMPONENTE EditVariantModal (EXTRAÍDO) ===
// ==================================================
const EditVariantModal = ({ variant, isOpen, onClose, onSaveSuccess }) => {
    // Hooks chamados no nível superior deste componente
    const [cost, setCost] = useState('');
    const [isNational, setIsNational] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [modalError, setModalError] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);
    const modalContentRef = useRef(null); // Ref para o listener de paste

    // Efeito para resetar/preencher o modal
    useEffect(() => {
        if (isOpen && variant) {
            setCost(variant.cost || '');
            setIsNational(variant.isNationalShipping || false);
            setPreviewUrl(variant.imageUrl ? `${BACKEND_URL}${variant.imageUrl}` : null); // Usa URL completa
            setSelectedFile(null);
            setModalError('');
            setIsSaving(false);
            setSaveSuccess(false);
        }
        if (!isOpen) {
            setPreviewUrl(null);
        }
    }, [variant, isOpen]); // Dependências corretas

    // Handler para seleção via input file
    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => { setPreviewUrl(reader.result); }; // Preview local (DataURL)
            reader.readAsDataURL(file);
        } else {
            setSelectedFile(null);
            setPreviewUrl(variant?.imageUrl ? `${BACKEND_URL}${variant.imageUrl}` : null); // Volta para original
        }
    };

    // Handler para o evento PASTE
    const handlePaste = useCallback((event) => {
        const clipboardItems = event.clipboardData?.items;
        if (!clipboardItems) return;
        console.log("Paste event detectado. Itens:", clipboardItems.length);
        for (let i = 0; i < clipboardItems.length; i++) {
            const item = clipboardItems[i];
            if (item.type.indexOf('image') !== -1) {
                const file = item.getAsFile();
                if (file) {
                    console.log("Imagem colada detectada:", file.name, file.type);
                    setSelectedFile(file); // Atualiza estado
                    const reader = new FileReader();
                    reader.onloadend = () => { setPreviewUrl(reader.result); console.log("Preview gerado para imagem colada."); };
                    reader.onerror = (err) => { console.error("Erro ao ler imagem colada:", err); };
                    reader.readAsDataURL(file); // Gera preview local
                    event.preventDefault();
                    return; // Para após encontrar a primeira imagem
                }
            }
        }
        console.log("Nenhuma imagem encontrada nos itens colados.");
    }, []); // useCallback sem dependências de estado, pois usa as funções setState que são estáveis

    // Efeito para adicionar/remover o listener de PASTE
    useEffect(() => {
        const modalElement = modalContentRef.current;
        if (isOpen && modalElement) {
            modalElement.addEventListener('paste', handlePaste);
            console.log("Paste listener ADICIONADO ao modal.");
        }
        return () => { // Função de limpeza
            if (modalElement) {
                modalElement.removeEventListener('paste', handlePaste);
                console.log("Paste listener REMOVIDO do modal.");
            }
        };
    }, [isOpen, handlePaste]); // Dependências corretas

    // Função de Salvar
    const handleSave = async () => {
        setIsSaving(true); setModalError(''); setSaveSuccess(false);
        const formData = new FormData();
        const costValue = cost === '' ? null : parseFloat(cost);

        if (cost !== '' && isNaN(costValue)) {
            setModalError('Custo inválido. Insira um número ou deixe em branco.');
            setIsSaving(false); return;
        }
        formData.append('cost', (costValue === null || cost === '') ? '' : costValue);
        formData.append('isNationalShipping', isNational);
        if (selectedFile) { formData.append('variantImage', selectedFile); }

        try {
            const response = await api.put(`/products/variants/${variant.variantId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setSaveSuccess(true);
            if (onSaveSuccess) { onSaveSuccess(response.data.variant); }
            setTimeout(() => { onClose(); }, 1500); // Fecha após delay
        } catch (err) {
             setModalError(err.response?.data?.message || 'Falha ao salvar alterações.');
             setSaveSuccess(false);
        } finally {
            setIsSaving(false);
        }
    };

    // Condição de Retorno Antecipado (DEPOIS dos hooks)
    if (!isOpen || !variant) return null;

    // --- JSX do Modal ---
    return (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-out">
             <div ref={modalContentRef} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto transform transition-all duration-300 ease-out scale-95 opacity-0 animate-fade-in-scale" style={{ animationName: 'fadeInScale', animationDuration: '0.2s', animationFillMode: 'forwards' }}>
                 <h3 className="text-lg font-semibold mb-1 text-gray-800">Editar Variante</h3>
                 <p className='text-sm text-gray-600 mb-4'>{variant.productTitle} - <span className='font-medium'>{variant.variantTitle}</span></p>
                 {modalError && <p className="text-sm text-red-600 mb-3">{modalError}</p>}
                 {saveSuccess && <p className="text-sm text-green-600 mb-3">Variante salva com sucesso!</p>}
                 <div className="space-y-4">
                     {/* Imagem Preview e Upload/Paste Area */}
                     <div
                         className='text-center border-2 border-dashed border-gray-300 p-4 rounded-lg hover:border-indigo-400 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 cursor-pointer relative'
                         title="Clique para escolher ou Cole (Cmd/Ctrl+V) a imagem aqui"
                         onClick={() => document.getElementById('variantImage')?.click()}
                         tabIndex={0} // Torna focável para paste
                     >
                         <label htmlFor="variantImage" className="block text-sm font-medium text-gray-700 mb-1 cursor-pointer">Imagem</label>
                         <div className='flex justify-center items-center mb-2 min-h-[6rem]'>
                             {previewUrl ? ( <img src={previewUrl} alt="Preview" className="h-24 w-24 object-cover rounded border" /> )
                                         : ( <PhotoIcon className="h-16 w-16 text-gray-300" /> )}
                         </div>
                         <input id="variantImage" name="variantImage" type="file" accept="image/*" onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                         <span className="text-xs text-gray-500 mt-1 block"> {selectedFile ? selectedFile.name : 'Clique ou cole para adicionar imagem'} </span>
                      </div>
                     {/* Custo (USD) */}
                     <div>
                         <label htmlFor="cost" className="block text-sm font-medium text-gray-700">Custo (USD)</label>
                         <input id="cost" name="cost" type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="Ex: 15.99" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                     </div>
                     {/* Envio Nacional */}
                     <div className="flex items-center">
                         <input id="isNationalShipping" name="isNationalShipping" type="checkbox" checked={isNational} onChange={(e) => setIsNational(e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" />
                         <label htmlFor="isNationalShipping" className="ml-2 block text-sm text-gray-900">Produto de Envio Nacional</label>
                     </div>
                 </div>
                 {/* Botões */}
                 <div className="mt-6 flex justify-end space-x-3">
                     <button type="button" onClick={onClose} disabled={isSaving} className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"> Cancelar </button>
                     <button type="button" onClick={handleSave} disabled={isSaving} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"> {isSaving ? 'Salvando...' : 'Salvar'} </button>
                 </div>
             </div>
             <style>{`@keyframes fadeInScale { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } } .animate-fade-in-scale { animation: fadeInScale 0.2s ease-out forwards; } `}</style>
         </div>
     );
};
// ========================================
// === FIM DO COMPONENTE EditVariantModal ===
// ========================================


// ==============================================
// === COMPONENTE ProductsPage (PRINCIPAL) ===
// ==============================================
const ProductsPage = () => {
    // --- Hooks ---
    const [variants, setVariants] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [limit] = useState(15);
    const [searchTerm, setSearchTerm] = useState('');
    const [needsInfoFilter, setNeedsInfoFilter] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [nationalShippingFilter, setNationalShippingFilter] = useState(false); // <<< NOVO ESTADO (default: false)
    // Não precisamos mais de API_BASE_URL_FOR_IMAGES aqui se BACKEND_URL for global ou definido aqui

    // --- Funções ---
    const fetchVariants = useCallback(async () => {
        setIsLoading(true); setError('');
        console.log(`Buscando variantes: page=${currentPage}, limit=${limit}, needsInfo=${needsInfoFilter}, isNational=${nationalShippingFilter}, search=${searchTerm}`);
        try {
            const params = new URLSearchParams({
                page: currentPage, limit: limit, needsInfo: needsInfoFilter,
                sortBy: 'createdAt', sortOrder: 'DESC',
            });
            if (searchTerm) params.append('search', searchTerm);
            // <<< ADICIONA PARÂMETRO isNational SE O FILTRO ESTIVER ATIVO >>>
            if (nationalShippingFilter) {
                params.append('isNational', 'true');
            }
            const response = await api.get(`/products/variants?${params.toString()}`);
            setVariants(response.data.items || []);
            setTotalPages(response.data.totalPages || 1);
            setTotalItems(response.data.totalItems || 0);
            setCurrentPage(response.data.currentPage || 1);
        } catch (err) {
            setError(err.response?.data?.message || 'Falha ao carregar.'); setVariants([]); setTotalPages(1); setTotalItems(0);
        } finally { setIsLoading(false); }
    }, [currentPage, limit, needsInfoFilter, searchTerm, nationalShippingFilter]);

    const handlePageChange = (newPage) => {
         if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) setCurrentPage(newPage);
         else if (newPage < 1) setCurrentPage(1);
         else if (newPage > totalPages) setCurrentPage(totalPages);
    };
    const handleSearchChange = (event) => setSearchTerm(event.target.value);
    const handleSearchSubmit = (event) => { event.preventDefault(); setCurrentPage(1); fetchVariants(); };
    const handleNeedsInfoToggle = () => { setNeedsInfoFilter(prev => !prev); setCurrentPage(1); };
    // <<< NOVO HANDLER para o toggle "Envio Nacional" >>>
    const handleNationalShippingToggle = () => {
        setNationalShippingFilter(prev => !prev);
        setCurrentPage(1); // Reseta página
        // fetchVariants será chamado pelo useEffect
    };
    const openEditModal = (variant) => { setSelectedVariant(variant); setIsModalOpen(true); };
    const closeEditModal = () => { setIsModalOpen(false); setSelectedVariant(null); };
    const handleSaveSuccess = (updatedVariant) => {
        setVariants(prevVariants => prevVariants.map(v => v.variantId === updatedVariant.variantId ? updatedVariant : v ));
    };

    // --- Efeito ---
    useEffect(() => { fetchVariants(); }, [fetchVariants]);

    // --- JSX ---
    return (
        <div className="p-4 md:p-6 lg:p-8">
            <h1 className="text-2xl font-semibold text-gray-800 mb-4">
                Gerenciamento de Produtos/Variantes
            </h1>

            {/* Área de Filtros e Busca */}
            <div className="mb-4 p-4 bg-white rounded shadow-md">
                {/* Ajusta o grid para acomodar mais um filtro */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                    {/* Toggle "Precisa de Info" */}
                    <div className="flex items-center space-x-2">
                        <input id="needsInfo" type="checkbox" checked={needsInfoFilter} onChange={handleNeedsInfoToggle} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"/>
                        <label htmlFor="needsInfo" className="text-sm font-medium text-gray-700">Precisa Info</label> {/* Texto reduzido */}
                    </div>

                    {/* --- NOVO CHECKBOX: Envio Nacional --- */}
                    <div className="flex items-center space-x-2">
                        <input
                            id="nationalShippingFilter"
                            type="checkbox"
                            checked={nationalShippingFilter}
                            onChange={handleNationalShippingToggle} // Usa o novo handler
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor="nationalShippingFilter" className="text-sm font-medium text-gray-700">
                            Apenas Envio Nacional
                        </label>
                    </div>
                    {/* ------------------------------------ */}

                    {/* Busca (ocupa 2 colunas em telas maiores) */}
                     <form onSubmit={handleSearchSubmit} className="sm:col-span-2 lg:col-span-2"> {/* Ajusta col-span */}
                        <label htmlFor="variantSearch" className="block text-sm font-medium text-gray-700 mb-1">Buscar</label> {/* Label simplificada */}
                        <div className="flex">
                            <input id="variantSearch" type="text" value={searchTerm} onChange={handleSearchChange} placeholder="Nome, SKU, Produto..." className="flex-grow ..."/>
                            <button type="submit" className="... ">Buscar</button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Exibição de Erro Principal */}
            {error && !isLoading && ( <div className="mb-4 p-4 bg-red-100 text-red-700 border border-red-400 rounded">{error}</div> )}

            {/* Tabela/Grid de Variantes */}
             <div className="bg-white rounded shadow-md overflow-x-auto">
                {isLoading ? ( <div className="text-center p-10">Carregando variantes...</div> )
                 : variants.length === 0 ? ( <div className="text-center p-10 text-gray-500">Nenhuma variante encontrada com os filtros atuais.</div> )
                 : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                             <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Imagem</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produto</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variante</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Custo (USD)</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Env. Nac.</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                         <tbody className="bg-white divide-y divide-gray-200">
                            {variants.map((variant) => (
                                <tr key={variant.variantId}>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                         <div className='relative h-12 w-12'>
                                            {variant.imageUrl ? ( <img src={`${BACKEND_URL}${variant.imageUrl}`} alt="Variante" className="h-full w-full object-cover rounded border" onError={(e) => { e.target.style.display='none'; }} /> )
                                                : ( <div className="h-full w-full bg-gray-200 rounded flex items-center justify-center text-gray-400"><PhotoIcon className='h-6 w-6'/></div> )}
                                            {(!variant.imageUrl || variant.cost === null) && ( <ExclamationCircleIcon className={`h-5 w-5 absolute -bottom-1 -right-1 rounded-full bg-white ${!variant.imageUrl && variant.cost === null ? 'text-red-500' : 'text-yellow-500'}`} title={!variant.imageUrl && variant.cost === null ? "Imagem e Custo faltando" : (!variant.imageUrl ? "Imagem faltando" : "Custo faltando")} /> )}
                                         </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700 max-w-xs truncate" title={variant.productTitle}>{variant.productTitle}</td>
                                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 max-w-xs truncate" title={variant.variantTitle}>{variant.variantTitle}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900"> {variant.cost !== null ? `$${parseFloat(variant.cost).toFixed(2)}` : <span className='text-yellow-600 italic text-xs'>Pendente</span>} </td>
                                     <td className="px-4 py-2 whitespace-nowrap text-center text-sm"> {variant.isNationalShipping ? <CheckCircleIcon className='h-5 w-5 text-green-500 mx-auto'/> : <span className='text-gray-400 text-xs'>Não</span> } </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-center text-sm font-medium"> <button onClick={() => openEditModal(variant)} className="text-indigo-600 hover:text-indigo-900" title="Editar Variante"> <PencilSquareIcon className='h-5 w-5 mx-auto'/> </button> </td>
                                </tr>
                            ))}
                         </tbody>
                    </table>
                 )}
             </div>

            {/* Paginação */}
            <div className="mt-4 flex flex-wrap justify-between items-center px-4 py-3 bg-white border-t border-gray-200 sm:px-6 rounded-b shadow-md gap-2">
                 <div className="text-sm text-gray-700"> Mostrando {isLoading? 0 : variants.length > 0 ? (currentPage - 1) * limit + 1 : 0} - {isLoading? 0 : Math.min(currentPage * limit, totalItems)} de {totalItems} variantes </div>
                 <div className="flex items-center gap-1">
                     <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1 || isLoading} className="relative inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"> Anterior </button>
                     <span className="mx-2 text-sm text-gray-700">Página {currentPage} de {totalPages}</span>
                     <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages || isLoading} className="relative inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"> Próxima </button>
                 </div>
            </div>

            {/* Modal de Edição */}
            <EditVariantModal
                isOpen={isModalOpen}
                onClose={closeEditModal}
                variant={selectedVariant}
                onSaveSuccess={handleSaveSuccess}
            />
        </div>
    );
};
// =====================================
// === FIM DO COMPONENTE ProductsPage ===
// =====================================

export default ProductsPage;